import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { FiSearch, FiUsers, FiEye, FiPause, FiPlay, FiTrash2, FiRefreshCw, FiZap, FiXCircle, FiCopy, FiAlertTriangle } from 'react-icons/fi';

// Helper function to get proper logo URL
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  
  // If it's already a full URL, return it
  if (logoPath.startsWith('http')) {
    return logoPath;
  }
  
  // If it's a relative path starting with /uploads/
  if (logoPath.startsWith('/uploads/')) {
    return `${axios.defaults.baseURL}${logoPath}`;
  }
  
  // If it contains /uploads/ but doesn't start with it (extract the path)
  if (logoPath.includes('/uploads/')) {
    const uploadPath = logoPath.substring(logoPath.indexOf('/uploads/'));
    return `${axios.defaults.baseURL}${uploadPath}`;
  }
  
  // Default case: prepend the base URL
  return `${axios.defaults.baseURL}${logoPath}`;
};

const AdminTeams = () => {
  const confirm = useConfirm();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [billingFilter, setBillingFilter] = useState('all');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/teams');
      setTeams(response.data.teams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTeams = teams
    .filter(team => 
      (team.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.industry || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(team => {
      if (statusFilter === 'active') return team.isActive;
      if (statusFilter === 'suspended') return !team.isActive;
      return true;
    })
    .filter(team => {
      if (billingFilter === 'active') return team.paymentStatus === 'active' || team.subscriptionStatus === 'active';
      if (billingFilter === 'cancelled') return team.paymentStatus === 'cancelled' || team.subscriptionStatus === 'canceled';
      if (billingFilter === 'overdue') return team.paymentStatus === 'overdue' || team.subscriptionStatus === 'past_due';
      return true;
    })
    .filter(team => {
      if (!attentionOnly) return true;
      return getAttentionFlags(team).length > 0;
    });

  const handleViewTeam = (team) => {
    setSelectedTeam(team);
    setStripeInfo(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTeam(null);
    setStripeInfo(null);
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  };

  const isFuture = (date) => {
    if (!date) return false;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() > Date.now();
  };

  const isPast = (date) => {
    if (!date) return false;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() < Date.now();
  };

  const getAttentionFlags = (team) => {
    const flags = [];
    if (team?.adminOverrideUntil && isFuture(team.adminOverrideUntil)) flags.push('Override');
    if (team?.gracePeriodEndsAt && isFuture(team.gracePeriodEndsAt)) flags.push('Grace');
    if (team?.nextPaymentDate && isPast(team.nextPaymentDate) && (team.subscriptionStatus === 'active' || team.subscriptionStatus === 'canceled')) flags.push('Expired');
    if (team?.subscriptionStatus === 'active' && team?.paymentStatus && team.paymentStatus !== 'active') flags.push('Mismatch');
    return flags;
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const handleClearOverride = async (teamId) => {
    const ok = await confirm({
      title: 'Clear admin override and grace flags?',
      description: 'This removes any manual billing overrides for the team.',
      confirmLabel: 'Clear',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.put(`/api/admin/teams/${teamId}/clear-override`);
      await fetchTeams();
      toast.success('Override cleared');
    } catch (error) {
      console.error('Error clearing override:', error);
      toast.error(error.response?.data?.message || 'Failed to clear override');
    }
  };

  const handleRefreshStripe = async (teamId) => {
    try {
      setStripeLoading(true);
      const res = await axios.get(`/api/admin/teams/${teamId}/stripe`);
      setStripeInfo(res.data);
      toast.success('Stripe status refreshed');
    } catch (error) {
      console.error('Error refreshing stripe:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh Stripe');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSyncStripe = async (teamId) => {
    const ok = await confirm({
      title: 'Sync billing fields from Stripe?',
      description: 'This refreshes the team’s billing fields from Stripe and clears any override or grace flags.',
      confirmLabel: 'Sync',
    });
    if (!ok) return;
    try {
      setStripeLoading(true);
      await axios.put(`/api/admin/teams/${teamId}/sync-stripe`);
      await fetchTeams();
      toast.success('Team synced from Stripe');
    } catch (error) {
      console.error('Error syncing stripe:', error);
      toast.error(error.response?.data?.message || 'Failed to sync from Stripe');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSuspendTeam = async (teamId) => {
    const ok = await confirm({
      title: 'Suspend this team?',
      description: 'Members will lose access until the team is reactivated.',
      confirmLabel: 'Suspend',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.put(`/api/admin/teams/${teamId}/suspend`);
      fetchTeams();
      toast.success('Team suspended successfully');
    } catch (error) {
      console.error('Error suspending team:', error);
      toast.error('Failed to suspend team');
    }
  };

  const handleActivateTeam = async (teamId) => {
    try {
      await axios.put(`/api/admin/teams/${teamId}/activate`);
      fetchTeams();
      toast.success('Team activated successfully');
    } catch (error) {
      console.error('Error activating team:', error);
      toast.error('Failed to activate team');
    }
  };
  
  const handleDeleteTeam = async (teamId) => {
    const ok = await confirm({
      title: 'Permanently delete this team?',
      description: 'This action cannot be undone. All team data will be removed.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      setIsDeleting(true);
      await axios.delete(`/api/admin/teams/${teamId}`);
      fetchTeams();
      toast.success('Team deleted successfully');
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <GlassCard className="p-8 text-center max-w-md mx-auto mt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading teams...</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Manage Teams
          </h1>
          <p className="text-gray-600 dark:text-gray-400">View and manage all teams in your organization</p>
        </div>
        
        {/* Search and Filter */}
        <GlassCard className="p-6 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search teams by name or industry..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-12 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm text-gray-900 dark:text-white"
              >
                <option value="all">All statuses</option>
                <option value="active">Active teams</option>
                <option value="suspended">Suspended teams</option>
              </select>
            </div>
            <div>
              <select
                value={billingFilter}
                onChange={(e) => setBillingFilter(e.target.value)}
                className="w-full px-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm text-gray-900 dark:text-white"
              >
                <option value="all">All billing</option>
                <option value="active">Billing active</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled/Canceled</option>
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={attentionOnly}
                  onChange={(e) => setAttentionOnly(e.target.checked)}
                  className="mr-2"
                />
                Needs attention
              </label>
            </div>
          </div>
        </GlassCard>
        
        {/* Teams List */}
        <GlassCard className="overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
              <FiUsers className="h-5 w-5 mr-2 text-blue-500" />
              Teams ({filteredTeams.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50 dark:divide-gray-700/50">
              <thead className="bg-gray-50/50 dark:bg-gray-800/50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Team
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Industry
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Members
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Subscription
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Billing
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredTeams.length > 0 ? (
              filteredTeams.map((team) => (
                <tr key={team.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        {team.logo ? (
                          <img 
                            src={getLogoUrl(team.logo)} 
                            alt={team.name} 
                            className="h-10 w-10 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span 
                          className="text-lg font-medium text-gray-500 dark:text-gray-400"
                          style={{ display: team.logo ? 'none' : 'block' }}
                        >
                          {team.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {team.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Created {new Date(team.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{team.industry}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {team.members?.length || 0} members
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {team.plan || 'Free'}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {team.members?.length || 0}/{team.planUserLimit || 0} licenses used
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          {team.paymentStatus || '—'}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          {team.subscriptionStatus || '—'}
                        </span>
                        {getAttentionFlags(team).length > 0 && (
                          <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            <FiAlertTriangle className="mr-1" />
                            {getAttentionFlags(team).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Next: {team.nextPaymentDate ? new Date(team.nextPaymentDate).toLocaleDateString() : '—'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${team.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                      {team.isActive ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewTeam(team)}
                      className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 mr-3"
                    >
                      View
                    </button>
                    {team.isActive ? (
                      <button
                        onClick={() => handleSuspendTeam(team.id)}
                        className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 mr-3"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateTeam(team.id)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTeam(team.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No teams found
                </td>
              </tr>
            )}
          </tbody>
            </table>
          </div>
        </GlassCard>

      {/* Team Details Modal */}
      {isModalOpen && selectedTeam && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center mr-4">
                          {selectedTeam.logo ? (
                            <img 
                              src={getLogoUrl(selectedTeam.logo)} 
                              alt={selectedTeam.name} 
                              className="h-12 w-12 rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <span className="text-xl font-medium text-gray-500 dark:text-gray-400">
                              {selectedTeam.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                          {selectedTeam.name}
                        </h3>
                      </div>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTeam.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                        {selectedTeam.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Industry</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.industry}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Created</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedTeam.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Subscription Plan</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.plan || 'Free'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">License Usage</h4>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedTeam.members?.length || 0}/{selectedTeam.planUserLimit || 0} licenses used
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Payment Status</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.paymentStatus || '—'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Subscription Status</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.subscriptionStatus || '—'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Last Payment</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(selectedTeam.lastPaymentDate)}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Next Payment</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(selectedTeam.nextPaymentDate)}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Grace Period Ends</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(selectedTeam.gracePeriodEndsAt)}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Admin Override Until</h4>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(selectedTeam.adminOverrideUntil)}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Stripe Customer</h4>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.stripeCustomerId || '—'}</p>
                          {selectedTeam.stripeCustomerId && (
                            <button
                              onClick={() => copyToClipboard(selectedTeam.stripeCustomerId)}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <FiCopy />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Stripe Subscription</h4>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-900 dark:text-white">{selectedTeam.stripeSubscriptionId || '—'}</p>
                          {selectedTeam.stripeSubscriptionId && (
                            <button
                              onClick={() => copyToClipboard(selectedTeam.stripeSubscriptionId)}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <FiCopy />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRefreshStripe(selectedTeam.id)}
                        disabled={stripeLoading}
                      >
                        <FiRefreshCw className="inline mr-2" />
                        Refresh Stripe
                      </GlassButton>
                      <GlassButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleSyncStripe(selectedTeam.id)}
                        disabled={stripeLoading}
                      >
                        <FiZap className="inline mr-2" />
                        Sync from Stripe
                      </GlassButton>
                      <GlassButton
                        variant="danger"
                        size="sm"
                        onClick={() => handleClearOverride(selectedTeam.id)}
                        disabled={stripeLoading}
                      >
                        <FiXCircle className="inline mr-2" />
                        Clear Override
                      </GlassButton>
                    </div>

                    {stripeInfo?.stripe && (
                      <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Stripe (Live)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Status</h5>
                            <p className="text-sm text-gray-900 dark:text-white">{stripeInfo.stripe.status}</p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Cancel at Period End</h5>
                            <p className="text-sm text-gray-900 dark:text-white">{stripeInfo.stripe.cancel_at_period_end ? 'Yes' : 'No'}</p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current Period Start</h5>
                            <p className="text-sm text-gray-900 dark:text-white">{stripeInfo.stripe.current_period_start ? new Date(stripeInfo.stripe.current_period_start * 1000).toLocaleString() : '—'}</p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current Period End</h5>
                            <p className="text-sm text-gray-900 dark:text-white">{stripeInfo.stripe.current_period_end ? new Date(stripeInfo.stripe.current_period_end * 1000).toLocaleString() : '—'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Team Members</h4>
                    <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-md">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              User
                            </th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Role
                            </th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Joined
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {selectedTeam.members && selectedTeam.members.length > 0 ? (
                            selectedTeam.members.map((member) => (
                              <tr key={member.id}>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                      {member.avatar ? (
                                        <img src={`/api${member.avatar}`} alt={`${member.firstName} ${member.lastName}`} className="h-8 w-8 rounded-full" />
                                      ) : (
                                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                          {member.firstName ? member.firstName.charAt(0).toUpperCase() : '?'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="ml-3">
                                      <div className="flex items-center">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                          {member.firstName} {member.lastName}
                                        </p>
                                        {member.role === 'super_admin' && (
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                            Super Admin
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {member.email}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {member.role === 'super_admin' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                      <svg className="-ml-0.5 mr-1 h-2 w-2 text-purple-400" fill="currentColor" viewBox="0 0 8 8">
                                        <circle cx="4" cy="4" r="3" />
                                      </svg>
                                      Super Admin
                                    </span>
                                  ) : member.role === 'team_admin' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      Team Admin
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Team Member</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${member.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                                    {member.isActive ? 'Active' : 'Suspended'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                  {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                No members found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 text-right">
                <button
                  onClick={handleCloseModal}
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminTeams;
